import { useTrans, useResolveUploadedPath } from "../hooks";
import {
    Text,
    Card,
    Stack,
    CardBody,
    Box,
    HStack,
    IconButton,
} from "@chakra-ui/react";
import { Company, Partnership, School, User } from "../apis";
import { APPC } from "../config";
import imagePlaceholder from "../assets/images/imagePlaceholder.svg";
import { AppIcon } from "./AppIcon";

interface AppWidgetCardProps {
    data: Company | School;
    partnership?: Partnership;
    actionElement?: JSX.Element;
}

export const AppWidgetCard = ({
    data,
    partnership,
    actionElement,
}: AppWidgetCardProps) => {
    const { t } = useTrans();

    const fileType =
        data["@type"] === "Company"
            ? APPC.BE.Upload.FILETYPE.FILETYPE_COMPANY_POSTER
            : APPC.BE.Upload.FILETYPE.FILETYPE_SCHOOL_POSTER;

    const openGoogleMaps = () => {
        const mapsUrl = `https://www.google.com/maps?q=${data?.latitude},${data?.longitude}`;
        window.open(mapsUrl, "_blank");
    };

    return (
        <Card
            direction={{
                base: "column",
                lg: "column",
                xl: "row",
            }}
            overflow="hidden"
            variant="outline"
            bg={"var(--chakra-colors-primaryT95)"}
            borderRadius={8}
            borderColor={"var(--chakra-colors-primaryT80)"}
            w={"full"}
        >
            <Box
                minW={{ base: "full", md: "full", lg: "56.25vw", xl: "318px" }}
                h={{
                    base: "56.25vw",
                    md: "56.25vw",
                    lg: "56.25vw",
                    xl: "180px",
                }}
                backgroundImage={
                    data?.imageName
                        ? useResolveUploadedPath(fileType, data?.imageName)
                        : imagePlaceholder
                }
                backgroundColor={"var(--chakra-colors-white)"}
                backgroundSize={"cover"}
                backgroundRepeat={"no-repeat"}
                backgroundPosition={"center"}
                borderRight={"1px solid var(--chakra-colors-primaryT80)"}
            ></Box>
            <CardBody>
                <HStack
                    justify="space-between"
                    w="full"
                    flexWrap={"wrap"}
                    alignItems={"flex-start"}
                    gap={3}
                >
                    <Text textStyle={"heading5"} fontWeight={600}>
                        {data?.name}
                    </Text>
                    {actionElement && actionElement}
                </HStack>
                <Box
                    py={{
                        base: 6,
                        lg: 6,
                        xl: 3,
                    }}
                >
                    <Text textStyle={"mediumSm"}>
                        {typeof data?.category === "object" &&
                        data?.category?.name
                            ? data.category.name
                            : ""}
                    </Text>
                </Box>
                <Stack
                    gap={{
                        base: 6,
                        lg: 6,
                        xl: 8,
                    }}
                    direction={{
                        base: "column",
                        sm: "column",
                        lg: "column",
                        xl: "row",
                    }}
                >
                    <Box>
                        <Text textStyle={"regularXs"}>
                            {t("ent.Company:region.label")}
                        </Text>
                        <Text textStyle={"mediumMd"} noOfLines={1}>
                            {(typeof data?.region === "object" &&
                                data?.region?.name) ??
                                "-"}
                        </Text>
                    </Box>
                    <Box>
                        <Text textStyle={"regularXs"}>
                            {t("ent.Company:department.label")}
                        </Text>
                        <Text textStyle={"mediumMd"} noOfLines={1}>
                            {(typeof data?.department === "object" &&
                                data?.department?.name) ??
                                "-"}
                        </Text>
                    </Box>
                    <Box>
                        <Text textStyle={"regularXs"}>
                            {t("ent.Company:city.label")}
                        </Text>
                        <Text textStyle={"mediumMd"} noOfLines={1}>
                            {data?.city ? data?.city : "-"}
                        </Text>
                    </Box>
                    {partnership && (
                        <Box>
                            <Text textStyle={"regularXs"}>
                                {t(
                                    "ent.Company:internshipReferenceTrainer.label"
                                )}
                            </Text>
                            <Text textStyle={"mediumMd"} noOfLines={1}>
                                {(
                                    partnership?.internshipReferenceTrainer as User
                                )?.fullName ?? "-"}
                            </Text>
                        </Box>
                    )}
                    {data?.latitude && data?.longitude && (
                        <IconButton
                            aria-label="Open in Google Maps"
                            variant={"unstyled"}
                            icon={
                                <AppIcon name="icl-location-pin" w="1.7rem" />
                            }
                            onClick={openGoogleMaps}
                        />
                    )}
                </Stack>
            </CardBody>
        </Card>
    );
};
